import { fetchEventSource } from '@microsoft/fetch-event-source';
import axios from 'axios';
import { addUserAIActivity, getAIAccessType, UserAIAccessType } from '../Dashboard/userAIActivity/UserAIActivityRepositories';
import gql from 'graphql-tag';
import { client } from '..';


type AIRequest = {
  prompt: string;
}

// Explicit request: the OpenAI key that used to be written here is removed - nothing may call OpenAI straight from the browser. The editor's AI
// buttons now go through the backend (see editorAIRequest below). The functions in this file that still call OpenAI directly (assistants, files,
// getResponseFromApi) have no key and will fail until they are moved to the backend as well.
const api_key = '';

export const ai_request = (request: AIRequest, respondWith: any) => {

  console.log('Request:', request);

  const systemMessage = [
    { role: 'system', content: 'Remove lines with ``` from the response start and response end.' }
  ];

  const messages = [
    ...systemMessage,
    { role: 'user', content: request.prompt }
  ];

  const openAiOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${api_key}`,
      'gpt-4o': 'org-lO8P5GN457AhwHGnDd5rIhbp',
      'Project': 'proj_3Ce7YpULhDWBCtut6WCnaMjj'
    },
    body: JSON.stringify({
      model: 'gpt-4o',
      temperature: 0.7,
      max_tokens: 4000,
      messages,
    })
  };

  respondWith.string((signal: any) => window.fetch('https://api.openai.com/v1/chat/completions', { signal, ...openAiOptions })
    .then(async (response) => {
      if (response) {
        const data = await response.json();
        if (data.error) {
          throw new Error(`${data.error.type}: ${data.error.message}`);
        } else if (response.ok) {
          // Extract the response content from the data returned by the API
          return data?.choices[0]?.message?.content?.trim();
        }
      } else {
        throw new Error('Failed to communicate with the ChatGPT API');
      }
    })
  );
};


// This example stores the API key in the client side integration. This is not recommended for any purpose.
// Instead, an alternate method for retrieving the API key should be used.
// const api_key = '<INSERT_API_KEY_HERE>';

const EditorAiResponseQuery = gql`
    query EditorAiResponse($emailBody: String, $queryText: String, $systemPromptOverride: String) {
        ai {
            emailSummaryResponse(emailBody: $emailBody, queryText: $queryText, systemPromptOverride: $systemPromptOverride) {
                id,
                summary
            }
        }
    }
`;

// Explicit request: the editor's AI buttons (TinyMCE's ai plugin) ask the backend instead of calling OpenAI from the browser. The backend's existing
// text query (the one the Outlook add-in uses) takes a system prompt, an instruction and a body of text and answers with plain text, with nothing kept
// at the AI provider. Only what the plugin itself supplies is sent: its own system messages (which ask for HTML), the user's instruction, the text the
// instruction applies to and the earlier turns of this conversation - nothing is added from lookups. The answer arrives in one piece, not streamed.
export const editorAIRequest = (request: any, respondWith: any) => {
  respondWith.string((signal: AbortSignal) => {
    addUserAIActivity({
      accessType: getAIAccessType(UserAIAccessType.CRMTextEditor),
      accessSubType: 'CRM Response Email',
      question: request.prompt
    });

    // Without a system prompt the backend would use its own "summarise this email" one, so there is always one.
    const systemPrompt = [
      ...(request.system || []),
      'Remove lines with ``` from the response start and response end.'
    ].join('\n\n');

    // Earlier turns are sent as text, so the instruction can refer to them ("make it shorter").
    const previousTurns = (request.thread || [])
      .filter((event: any) => event.response)
      .map((event: any) => `Previous request: ${event.request.query}\nPrevious response: ${event.response.data}`)
      .join('\n\n');

    const textToWorkOn = request.context && request.context.length > 0
      ? `Text to work on: """${request.context}"""`
      : '';

    // The backend needs some body text; with nothing else to send, the instruction itself is the body.
    const emailBody = [previousTurns, textToWorkOn].filter(Boolean).join('\n\n');

    return client
      .query({
        query: EditorAiResponseQuery,
        variables: {
          emailBody: emailBody || request.query,
          queryText: emailBody ? request.query : null,
          systemPromptOverride: systemPrompt
        },
        fetchPolicy: 'network-only',
        context: { fetchOptions: { signal } }
      })
      // tslint:disable-next-line: no-any
      .then((results: { data: any }) => {
        const summary = results.data?.ai?.emailSummaryResponse?.summary;
        if (!summary) {
          throw new Error('The AI did not return a response.');
        }
        return summary;
      });
  });
};

export const streamingAIRequest = (request: any, respondWith: any) => {
  respondWith.stream((signal: any, streamMessage: any) => {

    // Adds each previous query and response as individual messages
    const conversation = request.thread.flatMap((event: any) => {
      if (event.response) {
        return [
          { role: 'user', content: event.request.query },
          { role: 'assistant', content: event.response.data }
        ];
      } else {
        return [];
      }
    });

    // System messages provided by the plugin to format the output as HTML content.
    const pluginSystemMessages = request.system.map((content: any) => ({
      role: 'system',
      content
    }));

    const systemMessages = [
      ...pluginSystemMessages,
      // Additional system messages to control the output of the AI
      { role: 'system', content: 'Remove lines with ``` from the response start and response end.' }
    ]

    // Forms the new query sent to the API
    const content = request.context.length === 0 || conversation.length > 0
      ? request.query
      : `Question: ${request.query} Context: """${request.context}"""`;

    const messages = [
      ...conversation,
      ...systemMessages,
      { role: 'user', content }
    ];

    const requestBody = {
      model: 'gpt-4o',
      temperature: 0.7,
      max_tokens: 800,
      messages,
      stream: true
    };

    const openAiOptions = {
      signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${api_key}`,
        'gpt-4o': 'org-lO8P5GN457AhwHGnDd5rIhbp',
        'Project': 'proj_3Ce7YpULhDWBCtut6WCnaMjj'
      },
      body: JSON.stringify(requestBody)
    };

    const onopen = async (response: any) => {
      if (response) {
        addUserAIActivity({
          accessType: getAIAccessType(UserAIAccessType.CRMTextEditor),
          accessSubType: 'CRM Response Email',
          question: request.prompt
        })

        const contentType = response.headers.get('content-type');
        if (response.ok && contentType?.includes('text/event-stream')) {
          return;
        } else if (contentType?.includes('application/json')) {
          const data = await response.json();
          if (data.error) {
            throw new Error(`${data.error.type}: ${data.error.message}`);
          }
        }
      } else {
        throw new Error('Failed to communicate with the ChatGPT API');
      }
    };

    // This function passes each new message into the plugin via the `streamMessage` callback.
    const onmessage = (ev: any) => {
      const data = ev.data;
      if (data !== '[DONE]') {
        const parsedData = JSON.parse(data);
        const firstChoice = parsedData?.choices[0];
        const message = firstChoice?.delta?.content;
        if (message) {
          streamMessage(message);
        }
      }
    };

    const onerror = (error: any) => {
      // Stop operation and do not retry by the fetch-event-source
      throw error;
    };

    // Use microsoft's fetch-event-source library to work around the 2000 character limit
    // of the browser `EventSource` API, which requires query strings

    return fetchEventSource('https://api.openai.com/v1/chat/completions', {
      ...openAiOptions,
      openWhenHidden: true,
      onopen,
      onmessage,
      onerror
    });

  });
};

type ChatGptResponseData = {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Choice[];
  usage: Usage;
  system_fingerprint: string;
};

type Choice = {
  index: number;
  message: Message;
  logprobs: null;
  finish_reason: string;
};

type Message = {
  role: string;
  content: string;
  refusal: null;
};

type Usage = {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
};

export type ChatGptResponseParamsMessage = {
  role: 'system' | 'user';
  content: string | any;
};

type ChatGptResponseParams = {
  messages: ChatGptResponseParamsMessage[];
};


export async function getResponseFromApi(params: ChatGptResponseParams) {
  try {
    const result = await axios.post<ChatGptResponseData>(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 4000,
        messages: params.messages
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${api_key}`,
          'gpt-4o': 'org-lO8P5GN457AhwHGnDd5rIhbp',
          'Project': 'proj_3Ce7YpULhDWBCtut6WCnaMjj',
        },
      }
    );

    return result.data;

  } catch (error) {
    console.error(error);
  }

  // Add a return statement here
  return null;

}


// // This example stores the API key in the client side integration. This is not recommended for any purpose.

const OPEN_API_URL_V1 = 'https://api.openai.com/v1';

const DEFAULT_HEADERS = {
  'Authorization': `Bearer ${api_key}`
}


interface FileUploadResponse {
  object: string;
  id: string;
  purpose: string;
  filename: string;
  bytes: number;
  created_at: number;
  expires_at: number | null;
  status: string;
  status_details: string | null;
}

/**
 * Files are used to upload documents that can be used with features like Assistants, Fine-tuning, and Batch API.
 * @param file 
 * @returns 
 */
export async function fileUpload(file: File) {

  try {
    const result = await axios.post<FileUploadResponse>(
      `${OPEN_API_URL_V1}/files`,
      {
        purpose: "assistants",
        file: file,
      },
      {
        headers: {
          ...DEFAULT_HEADERS,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return result.data;

  } catch (error) {
    console.error(error);
    return null;
  }

}

interface ThreadResponse {
  id: string;
  object: string;
  created_at: number;
  metadata: object;
  tool_resources: object;
}

/**
 * Create threads that assistants can interact with.
 * @returns 
 */
export async function createThread() {
  try {

    const result = await axios.post<ThreadResponse>(
      `${OPEN_API_URL_V1}/threads`,
      undefined,
      {
        headers: {
          ...DEFAULT_HEADERS,
          'OpenAI-Beta': 'assistants=v2',
          'Content-Type': 'application/json',
        },
      }
    );

    return result.data;

  } catch (error) {
    console.error(error);
    return null;
  }
}

interface AssistantResponse {
  id: string;
  object: string;
  created_at: number;
  name: string;
  description: string | null;
  model: string;
  instructions: string | null;
  tools: object[];
  top_p: number;
  temperature: number;
  reasoning_effort: string | null;
  tool_resources: object;
  metadata: object;
  response_format: string;
}

/**
 * Create an assistant with a model and instructions.
 * @returns 
 */
export async function createAssistants() {
  try {

    const result = await axios.post<AssistantResponse>(
      `${OPEN_API_URL_V1}/assistants`,
      {
        model: 'gpt-4o',
        name: 'CSV Analyzer',
        tools: [{
          type: 'code_interpreter'
        }],
      },
      {
        headers: {
          ...DEFAULT_HEADERS,
          'OpenAI-Beta': 'assistants=v2',
          'Content-Type': 'application/json',
        },
      }
    );

    return result.data;

  } catch (error) {
    console.error(error);
    return null;
  }
}

interface CreateThreadMessageParams {
  thread_id: string;
  role: 'user' | 'assistant';
  content: string;
  attachments?: {
    file_id: string;
    tools?: {
      type: 'code_interpreter' | string;
    }[];
  }[];
}

interface ThreadMessageResponse {
  id: string;
  object: 'thread.message';
  created_at: number;
  assistant_id: string | null;
  thread_id: string;
  run_id: string | null;
  role: 'user' | 'assistant';
  content: {
    type: 'text';
    text: {
      value: string;
      annotations: any[]; // Can be typed more strictly if annotation structure is known
    };
  }[];
  attachments: {
    file_id: string;
    tools: {
      type: 'code_interpreter' | string;
    }[];
  }[];
  metadata: Record<string, any>;
}

export async function createThreadMessage(params: CreateThreadMessageParams) {

  const { thread_id, ...rest } = params;

  try {
    const result = await axios.post<ThreadMessageResponse>(
      `${OPEN_API_URL_V1}/threads/${params.thread_id}/messages`,
      {
        ...rest
      },
      {
        headers: {
          ...DEFAULT_HEADERS,
          'OpenAI-Beta': 'assistants=v2',
          'Content-Type': 'application/json',
        },
      }
    );

    return result.data;

  } catch (error) {
    console.error(error);
    return null;
  }
}

interface CreateThreadRunParams {
  thread_id: string;
  assistant_id: string;
}

interface CreateThreadRunResponse {
  id: string;
  object: 'thread.run';
  created_at: number;
  assistant_id: string;
  thread_id: string;
  status:
  | 'queued'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'cancelling'
  | 'cancelled'
  | 'expired'; // inferred from common OpenAI status enums
  started_at: number | null;
  expires_at: number | null;
  cancelled_at: number | null;
  failed_at: number | null;
  completed_at: number | null;
  required_action: any | null; // Can be refined if schema is known
  last_error: any | null; // Can be refined if schema is known
  model: string;
  instructions: string | null;
  tools: {
    type: 'code_interpreter' | string;
  }[];
  tool_resources: Record<string, any>;
  metadata: Record<string, any>;
  temperature: number;
  top_p: number;
  reasoning_effort: number | null;
  max_completion_tokens: number | null;
  max_prompt_tokens: number | null;
  truncation_strategy: {
    type: 'auto' | string;
    last_messages: any | null;
  };
  incomplete_details: any | null;
  usage: any | null;
  response_format: 'auto' | 'text' | 'json' | string;
  tool_choice: 'auto' | 'none' | string;
  parallel_tool_calls: boolean;
}


export async function createThreadRun(params: CreateThreadRunParams) {
  const { thread_id, ...rest } = params;

  try {
    const result = await axios.post<CreateThreadRunResponse>(
      `${OPEN_API_URL_V1}/threads/${params.thread_id}/runs`,
      {
        ...rest
      },
      {
        headers: {
          ...DEFAULT_HEADERS,
          'OpenAI-Beta': 'assistants=v2',
          'Content-Type': 'application/json',
        },
      }
    );

    return result.data;

  } catch (error) {
    console.error(error);
    return null;
  }
}

interface GetThreadRunParams {
  thread_id: string;
  run_id: string;
}

interface GetThreadRunResponse {
  id: string;
  object: 'thread.run';
  created_at: number;
  assistant_id: string;
  thread_id: string;
  status:
  | 'queued'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'cancelling'
  | 'cancelled'
  | 'expired';
  started_at: number | null;
  expires_at: number | null;
  cancelled_at: number | null;
  failed_at: number | null;
  completed_at: number | null;
  required_action: any | null;
  last_error: any | null;
  model: string;
  instructions: string | null;
  tools: {
    type: 'code_interpreter' | string;
  }[];
  tool_resources: Record<string, any>;
  metadata: Record<string, any>;
  temperature: number;
  top_p: number;
  reasoning_effort: number | null;
  max_completion_tokens: number | null;
  max_prompt_tokens: number | null;
  truncation_strategy: {
    type: 'auto' | string;
    last_messages: any | null;
  };
  incomplete_details: any | null;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    prompt_token_details: {
      cached_tokens: number;
    };
    completion_tokens_details: {
      reasoning_tokens: number;
    };
  } | null;
  response_format: 'auto' | 'text' | 'json' | string;
  tool_choice: 'auto' | 'none' | string;
  parallel_tool_calls: boolean;
}

export async function getThreadRun(params: GetThreadRunParams) {
  try {
    const result = await axios.get<GetThreadRunResponse>(
      `${OPEN_API_URL_V1}/threads/${params.thread_id}/runs/${params.run_id}`,
      {
        headers: {
          ...DEFAULT_HEADERS,
          'OpenAI-Beta': 'assistants=v2',
        },
      }
    );

    return result.data;

  } catch (error) {
    console.error(error);
    return null;
  }
}

interface GetThreadMessagesParams {
  thread_id: string;
}

interface GetThreadMessagesResponse {
  object: 'list';
  data: ThreadMessage[];
  first_id: string;
  last_id: string;
  has_more: boolean;
}

interface ThreadMessage {
  id: string;
  object: 'thread.message';
  created_at: number;
  assistant_id: string | null;
  thread_id: string;
  run_id: string | null;
  role: 'user' | 'assistant';
  content: ThreadMessageContent[];
  attachments: ThreadAttachment[];
  metadata: Record<string, any>;
}

interface ThreadMessageContent {
  type: 'text';
  text: {
    value: string;
    annotations: any[]; // Replace with stricter typing if annotation format is known
  };
}

interface ThreadAttachment {
  file_id: string;
  tools: {
    type: 'code_interpreter' | string;
  }[];
}

export async function getThreadMessages(params: GetThreadMessagesParams) {
  try {
    const result = await axios.get<GetThreadMessagesResponse>(
      `${OPEN_API_URL_V1}/threads/${params.thread_id}/messages`,
      {
        headers: {
          ...DEFAULT_HEADERS,
          'OpenAI-Beta': 'assistants=v2',
        },
      }
    );

    return result.data;

  } catch (error) {
    console.error(error);
    return null;
  }
}

interface FileDeleteResponse {
  object: string;
  deleted: boolean;
  id: string;
}

export async function fileDelete(file_id: string) {
  try {
    const result = await axios.delete<FileDeleteResponse>(
      `${OPEN_API_URL_V1}/files/${file_id}`,
      {
        headers: {
          ...DEFAULT_HEADERS,
        },
      }
    );

    return result.data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

interface ThreadDeleteResponse {
  id: string;
  object: string;
  deleted: boolean;
}

export async function threadDelete(thread_id: string) {
  try {
    const result = await axios.delete<ThreadDeleteResponse>(
      `${OPEN_API_URL_V1}/threads/${thread_id}`,
      {
        headers: {
          ...DEFAULT_HEADERS,
          'OpenAI-Beta': 'assistants=v2',
        },
      }
    );

    return result.data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

interface AssistantDeleteResponse {
  id: string;
  object: string;
  deleted: boolean;
}

export async function assistantDelete(assistant_id: string) {
  try {
    const result = await axios.delete<AssistantDeleteResponse>(
      `${OPEN_API_URL_V1}/assistants/${assistant_id}`,
      {
        headers: {
          ...DEFAULT_HEADERS,
          'OpenAI-Beta': 'assistants=v2',
        },
      }
    );

    return result.data;
  } catch (error) {
    console.error(error);
    return null;
  }
}